import { useCallback, useEffect } from "react";

interface UseInfiniteScrollProps {
  hasMore: boolean;
  isLoading: boolean;
  loadMore: () => void;
  threshold?: number; // Distance from bottom to trigger load (in pixels)
}

export const useInfiniteScroll = ({
  hasMore,
  isLoading,
  loadMore,
  threshold = 300,
}: UseInfiniteScrollProps) => {
  const handleScroll = useCallback(() => {
    // Don't trigger if already loading or no more data
    if (isLoading || !hasMore) return;

    // Calculate scroll position
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollHeight = document.documentElement.scrollHeight;
    const clientHeight = window.innerHeight;

    // Check if user has scrolled close to the bottom
    const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);

    if (distanceFromBottom < threshold) {
      loadMore();
    }
  }, [hasMore, isLoading, loadMore, threshold]);

  useEffect(() => {
    // Add scroll event listener with throttling
    let timeoutId: NodeJS.Timeout;

    const throttledScrollHandler = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(handleScroll, 100); // Throttle to 100ms
    };

    window.addEventListener("scroll", throttledScrollHandler, {
      passive: true,
    });

    return () => {
      window.removeEventListener("scroll", throttledScrollHandler);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [handleScroll]);
};
